import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/lib/utils';

/**
 * Premium Button — adds subtle gradient backgrounds, layered shadows, a soft
 * lift on hover, and a press-down state on active to give every button real
 * tactile feedback. Variant names + sizes match the shadcn baseline so every
 * existing caller upgrades for free.
 *
 * The visual recipe (used by every coloured variant):
 *   - vertical gradient (from lighter top → slightly darker bottom)
 *   - 1px inset top highlight (simulates light hitting the top edge)
 *   - small drop shadow at rest, larger on hover
 *   - 1px upward translate on hover (lift), back to 0 on active (press)
 *   - all transitions use `ease-out` over 150ms
 */
const buttonVariants = cva(
  [
    // base
    'tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium',
    // Kill the UA stylesheet default `border: 2px outset buttonborder` —
    // Tailwind preflight is OFF in this project, so without explicit
    // `tw-border-0` the browser's 3D outset border bleeds through every
    // <button> and gives the chrome that "stone-ish / dark outline" look.
    // Variants that DO want a border (outline, ghost on hover) re-set
    // their own `tw-border tw-border-solid …`.
    'tw-border-0',
    // motion
    'tw-transition-all tw-duration-150 tw-ease-out',
    'hover:tw--translate-y-px active:tw-translate-y-0',
    // focus
    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2',
    // disabled
    'disabled:tw-pointer-events-none disabled:tw-opacity-50 disabled:tw-shadow-none disabled:hover:tw-translate-y-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'tw-bg-gradient-to-b tw-from-primary tw-to-primary/90 tw-text-primary-foreground',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.18)]',
          'hover:tw-from-primary/95 hover:tw-to-primary/80 hover:tw-shadow-md',
          'active:tw-shadow-sm active:tw-from-primary/90 active:tw-to-primary/95',
        ].join(' '),
        destructive: [
          'tw-bg-gradient-to-b tw-from-destructive tw-to-destructive/90 tw-text-destructive-foreground',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.18)]',
          'hover:tw-from-destructive/95 hover:tw-to-destructive/80 hover:tw-shadow-md',
          'active:tw-shadow-sm',
        ].join(' '),
        success: [
          'tw-bg-gradient-to-b tw-from-success tw-to-success/90 tw-text-success-foreground',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.18)]',
          'hover:tw-from-success/95 hover:tw-to-success/80 hover:tw-shadow-md',
          'active:tw-shadow-sm',
        ].join(' '),
        warning: [
          'tw-bg-gradient-to-b tw-from-warning tw-to-warning/90 tw-text-warning-foreground',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.20)]',
          'hover:tw-from-warning/95 hover:tw-to-warning/80 hover:tw-shadow-md',
          'active:tw-shadow-sm',
        ].join(' '),
        info: [
          // Light-blue / cyan accent. Matches the legacy `color="info"`
          // (#0dcaf0) used by Submit-for-Review and a few neutral actions.
          'tw-bg-gradient-to-b tw-from-sky-400 tw-to-sky-500 tw-text-white',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.25)]',
          'hover:tw-from-sky-400/95 hover:tw-to-sky-500/85 hover:tw-shadow-md',
          'active:tw-shadow-sm',
        ].join(' '),
        secondary: [
          'tw-bg-gradient-to-b tw-from-secondary tw-to-secondary/85 tw-text-secondary-foreground',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.30)]',
          'hover:tw-from-secondary/95 hover:tw-to-secondary/75 hover:tw-shadow-md',
          'active:tw-shadow-sm',
        ].join(' '),
        outline: [
          // `tw-border-solid` re-asserts border-style — base resets it to 0
          // and preflight is OFF, so without this the UA `outset` style
          // (or `inset` on inputs) would leak back in.
          'tw-border tw-border-solid tw-border-slate-200 tw-bg-gradient-to-b tw-from-background tw-to-muted/40 tw-text-foreground',
          'tw-shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.6)]',
          'hover:tw-border-slate-300 hover:tw-bg-accent hover:tw-text-accent-foreground hover:tw-shadow',
          'active:tw-shadow-sm',
        ].join(' '),
        ghost: [
          'tw-text-foreground',
          'hover:tw-bg-accent hover:tw-text-accent-foreground',
          // ghost is intentionally flat — no shadow/lift
          'hover:tw-translate-y-0',
        ].join(' '),
        link: [
          'tw-text-primary tw-underline-offset-4 hover:tw-underline',
          'hover:tw-translate-y-0',
        ].join(' '),
      },
      size: {
        default: 'tw-h-9 tw-px-4 tw-py-2',
        sm: 'tw-h-8 tw-rounded-md tw-px-3 tw-text-xs',
        lg: 'tw-h-10 tw-rounded-md tw-px-6',
        xl: 'tw-h-12 tw-rounded-md tw-px-8 tw-text-base',
        icon: 'tw-h-9 tw-w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
